import { z } from 'zod';

import {
  VaultId,
  GenerationId,
  CommitId,
  VaultType,
  OrganizationId,
  VaultProfileLockDto,
  VaultMessageType,
  ShareSecretId,
  VaultMemberSyncMode,
  EntraGroupId,
  ProfileId,
} from 'backend-client-web';

import { makeProtobufEnumSchema } from '../enumSchemas';
import { LinkedLoginUpdateMessageV1 } from '../inboxMessages/linkedLoginUpdateMessageV1';
import { LoginSummaryUpdateMessageV1 } from '../inboxMessages/loginSummaryUpdateMessageV1';
import { PersistableLoginMaintenanceCriteria } from '../loginMaintenanceCriteria/PersistableLoginMaintenanceCriteria';
import objectPassthrough from '../objectPassthrough';
import { uint8ArraySchema } from '../uint8ArraySchema';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';
import { undefinedFromNullish } from '../util/undefinedFromNullish';

const schemaVaultProfileLockDtoOldKeyNames = z
  .object({
    profileId: z.uuid(),
    profileKeyGenerationId: z.uuid(),
    encryptedStorableVaultKey: uint8ArraySchema,
    encryptedHighSecurityVaultKey: uint8ArraySchema,
    encryptedVaultMessagePrivateKey: undefinedFromNullish(uint8ArraySchema),
  })
  .transform(({ profileId, profileKeyGenerationId, ...rest }) => ({
    lockingProfileId: profileId,
    lockingProfileKeyGenerationId: profileKeyGenerationId,
    ...rest,
  }));

const schemaVaultProfileLockDtoNewKeyNames = z.object({
  lockingProfileId: z.uuid(),
  lockingProfileKeyGenerationId: z.uuid(),
  encryptedStorableVaultKey: uint8ArraySchema,
  encryptedHighSecurityVaultKey: uint8ArraySchema,
  encryptedVaultMessagePrivateKey: undefinedFromNullish(uint8ArraySchema),
});

const schemaVaultProfileLockDto = z.union([
  schemaVaultProfileLockDtoOldKeyNames,
  schemaVaultProfileLockDtoNewKeyNames,
]);

const schemaLinkedUpdateMessage = z.object({
  vaultId: z.uuid(),
  type: z.literal(VaultMessageType.LINKED_LOGIN_UPDATE_V1),
  message: LinkedLoginUpdateMessageV1,
});

interface LinkedUpdateMessage {
  vaultId: VaultId;
  type: typeof VaultMessageType.LINKED_LOGIN_UPDATE_V1;
  message: LinkedLoginUpdateMessageV1;
}

const schemaShareSecretUpdate = z.object({
  id: z.uuid(),
  encryptedHighSecurityVaultKey: uint8ArraySchema.optional(),
});

interface ShareSecretUpdate {
  id: ShareSecretId;
  encryptedPayload?: Uint8Array;
}

interface LoginSummaryUpdateMessage {
  type: typeof VaultMessageType.LOGIN_SUMMARY_UPDATE_V1;
  message: LoginSummaryUpdateMessageV1;
}

const LoginSummaryUpdateMessage = z.object({
  type: z.literal(VaultMessageType.LOGIN_SUMMARY_UPDATE_V1),
  message: LoginSummaryUpdateMessageV1,
});

interface PersistableVaultProfile {
  id: ProfileId;
}

interface PersistableVault {
  id: VaultId;
  type: WithUnknownValuePlaceholder<VaultType>;
  associatedVaultId?: VaultId | undefined;
  generationId: GenerationId;
  firstCommitId?: CommitId;
  commitId: CommitId;
  profiles: PersistableVaultProfile[];
  organizationId?: OrganizationId | undefined;
  profileLock?: VaultProfileLockDto;
  adminProfileLock?: VaultProfileLockDto;
  dirty?: boolean;
  vaultState: Uint8Array;
  stagedDiff?: Uint8Array | null;
  messagePublicKey: Uint8Array | undefined;
  inboxSlug: string | undefined;
  inboxEnabled: boolean;
  linkedUpdateMessages: LinkedUpdateMessage[];
  shareSecretUpdates: ShareSecretUpdate[];
  loginMaintenanceCriteria: PersistableLoginMaintenanceCriteria | null;
  memberSyncMode?: WithUnknownValuePlaceholder<VaultMemberSyncMode>;
  entraGroupId: EntraGroupId | undefined;
  loginSummaryUpdateMessages: LoginSummaryUpdateMessage[];
  // Only populated since encrypted persistables are supported.
  // TODO: Make non-optional once deployment is far enough. Old persistables
  // will then be dropped, but that's fine.
  vaultSecret?: Uint8Array;
}

const PersistableVaultProfile = objectPassthrough({
  id: z.string(),
});

const PersistableVault = z
  .object({
    id: z.uuid(),
    type: makeProtobufEnumSchema(VaultType, VaultType.UNKNOWN),
    associatedVaultId: undefinedFromNullish(z.uuid()).optional(),
    generationId: z.uuid(),
    firstCommitId: z.uuid().optional(),
    commitId: z.uuid(),
    profileIds: z.array(z.uuid()).optional(),
    profiles: z.array(PersistableVaultProfile).default([]),
    organizationId: undefinedFromNullish(z.uuid()).optional(),
    profileLock: schemaVaultProfileLockDto.optional(),
    adminProfileLock: schemaVaultProfileLockDto.optional(),
    dirty: z.boolean().optional(),
    vaultState: uint8ArraySchema,
    vaultSecret: uint8ArraySchema.optional(),
    stagedDiff: z.union([uint8ArraySchema, z.null()]).optional(),
    messagePublicKey: undefinedFromNullish(uint8ArraySchema),
    inboxSlug: undefinedFromNullish(z.string()),
    inboxEnabled: z.boolean().default(false),
    linkedUpdateMessages: z.array(schemaLinkedUpdateMessage).default([]),
    shareSecretUpdates: z.array(schemaShareSecretUpdate).default([]),
    loginStatusChangeTimes: PersistableLoginMaintenanceCriteria.nullable().optional(),
    loginMaintenanceCriteria: PersistableLoginMaintenanceCriteria.nullable().default(null),
    memberSyncMode: makeProtobufEnumSchema(
      VaultMemberSyncMode,
      VaultMemberSyncMode.NONE,
    ).optional(),
    entraGroupId: undefinedFromNullish(z.uuid()),
    loginSummaryUpdateMessages: z.array(LoginSummaryUpdateMessage).default([]),
  })
  .transform((pv) =>
    pv.loginStatusChangeTimes && !pv.loginMaintenanceCriteria ?
      {
        ...pv,
        loginStatusChangeTimes: undefined,
        loginMaintenanceCriteria: pv.loginStatusChangeTimes,
      }
    : pv,
  )
  .transform((pv) =>
    pv.profileIds ?
      {
        ...pv,
        profileIds: undefined,
        profiles: pv.profiles.length ? pv.profiles : (pv.profileIds?.map((id) => ({ id })) ?? []),
      }
    : pv,
  );

export { PersistableVault, PersistableVaultProfile };
